import { useMutation, useQueryClient } from '@tanstack/react-query';

import APIGateway from '@/api';
import { secretsKeys } from '@/hooks/queries/useSecrets';
import { CreateSecretReq, UpdateSecretGlobalReq, UpdateSecretReq } from '@/types/secret';

type UseSecretDeleteArgs = {
  secretId: string;
};

type UseSecretUpdateArgs = {
  secretId: string;
  body: UpdateSecretReq;
};

type UseSecretUpdateGlobalArgs = {
  secretId: string;
  body: UpdateSecretGlobalReq;
};

export function useSecretDelete() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { secretId }: UseSecretDeleteArgs ) => {
      return await APIGateway.secrets.secretDelete( secretId );
    },
    // Drop the now-orphan detail entry but DO NOT invalidate the list here:
    // the action menu (and its DeleteModal) lives inside a table row, so a
    // list refetch on success would unmount the row before the success step
    // is shown. The caller invalidates the list once the modal closes.
    onSuccess: ( _data, { secretId } ) => {
      queryClient.removeQueries( { queryKey: secretsKeys.detail( secretId ) } );
    }
  } );
}

export function useSecretCreate() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( body: CreateSecretReq ) => {
      return await APIGateway.secrets.secretCreate( body );
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: secretsKeys.all } );
    }
  } );
}

export function useSecretUpdate() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { secretId, body }: UseSecretUpdateArgs ) => {
      return await APIGateway.secrets.secretUpdate( secretId, body );
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: secretsKeys.all } );
    }
  } );
}

export function useSecretUpdateGlobal() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { secretId, body }: UseSecretUpdateGlobalArgs ) => {
      return await APIGateway.secrets.secretUpdateGlobal( secretId, body );
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: secretsKeys.all } );
    }
  } );
}
